/** @file validateDoc rules for sockets (§5.9): generated bones shaped and owned, a grip axis with length. */
import type { Doc } from './schema';
import type { BoneDoc } from './schemaParts/skeleton';
import { makeError, type Pose2DError } from '../pose2dError';

type Shape = { parent: string; kind: NonNullable<BoneDoc['kind']>; name: string };

/** What breaks the generated-bone shape, or null (a missing bone is NOT_FOUND elsewhere). */
function shapeProblem(bone: BoneDoc | undefined, want: Shape): string | null {
  if (!bone) return null;
  if (bone.parent !== want.parent) return `must be a child of ${want.parent}`;
  if (bone.kind !== want.kind) return `must have kind "${want.kind}"`;
  return bone.name === want.name ? null : `must be named "${want.name}"`;
}

/** VALIDATION for each bone marked as generated (kind socket / socketGrip2) that no socket names as its bone or grip-2 bone. */
function checkOwnedBones(doc: Doc, out: Pose2DError[]): void {
  const owned = new Set<string>();
  for (const s of Object.values(doc.sockets)) {
    owned.add(s.bone);
    if (s.grip2Bone !== undefined) owned.add(s.grip2Bone);
  }
  for (const [id, bone] of Object.entries(doc.bones)) {
    if (bone.kind === undefined || owned.has(id)) continue;
    out.push(makeError('VALIDATION', {}, { path: `bones.${id}.kind`, hint: `no socket owns this bone: kind "${bone.kind}" marks the bones socket.create generates (remove kind, or create the socket)` }));
  }
}

/**
 * Appends VALIDATION for sockets whose bone or grip-2 bone is not shaped as socket.create makes it, whose a and b
 * coincide (socket.create's gripSetup refuses that), and for generated-kind bones no socket owns.
 */
export function checkDocSockets(doc: Doc, out: Pose2DError[]): void {
  for (const [id, s] of Object.entries(doc.sockets)) {
    const checks: [field: string, boneId: string | undefined, want: Shape][] = [
      ['bone', s.bone, { parent: s.parentBone, kind: 'socket', name: s.name }],
      ['grip2Bone', s.grip2Bone, { parent: s.bone, kind: 'socketGrip2', name: `${s.name}-grip2` }],
    ];
    for (const [field, boneId, want] of checks) {
      const problem = boneId === undefined ? null : shapeProblem(doc.bones[boneId], want);
      if (problem !== null) out.push(makeError('VALIDATION', {}, { path: `sockets.${id}.${field}`, hint: `the socket ${field} ${problem}` }));
    }
    if (s.a[0] === s.b[0] && s.a[1] === s.b[1]) out.push(makeError('VALIDATION', {}, { path: `sockets.${id}.b`, hint: 'a and b must differ: the grip axis needs a direction' }));
  }
  checkOwnedBones(doc, out);
}
